import {
  Label,
  NonCopyableMarkdown,
  SectionWrapper,
  StaticOption,
  NonCopyableMarkdownOption,
  NonCopyableSpan,
} from '../common';
import React from 'react';
import ReactDOM from 'react-dom';
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  useDraggable,
  DragOverlay,
} from '@dnd-kit/core';
import styled from 'styled-components';
import { useDndScrollLock } from '../../../hooks/assessment-test/use-dnd-scroll-lock';
import {
  AssessmentTestContext,
  AssessmentTestContextType,
} from '../../../contexts/assessment-test';
import FollowUp from './FollowUp';
import { MultiLineInput } from '../../elements/Input';
import { SmallPortalTooltip } from '../../Tooltip';
import {
  TooltipContent,
  TooltipDetail,
  TooltipDetailHighlight,
  TooltipTitle,
} from '../../Admin/Progress/Tooltip';
import { useAutoAnimate } from '@formkit/auto-animate/react';

const MatrixContainer = styled.div`
  margin-top: 32px;
  margin-bottom: 24px;
  display: grid;
  grid-template-columns: 3fr 2fr;
  gap: 32px;
  align-items: start;
`;

const MatrixWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
`;

const AxisLabelsContainer = styled.div`
  display: flex;
  flex: 1;
  justify-content: space-around;
  align-items: stretch;
  gap: 12px;
  flex: 1;
  width: 100%;
`;

const VerticalAxisLabel = styled.div`
  writing-mode: vertical-lr;
  transform: rotate(180deg);
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textRegular};
  padding: 8px 0;
  user-select: none;
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const MatrixAndHorizontalLabel = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex: 1;
  gap: 8px;
`;

const MatrixGrid = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 1;
  border: 2px solid ${({ theme }) => theme.colors.bgActive};
  background: ${({ theme }) => theme.colors.bgDefaultSecondary};
  border-radius: 8px;
  overflow: visible;
`;

const MatrixInner = styled.div<{ $isEditing?: boolean }>`
  position: absolute;
  inset: 0;

  ${({ $isEditing }) =>
    $isEditing &&
    `
    /* Disable dragging of items when annotation bubble is open */
    > div[data-draggable] {
      pointer-events: none;
      cursor: default;
    }
  `}
`;

const AxisLine = styled.div<{ direction: 'horizontal' | 'vertical' }>`
  position: absolute;
  background-color: ${({ theme }) => theme.colors.textPlaceholder};
  ${({ direction }) =>
    direction === 'horizontal'
      ? `
    left: 0;
    right: 0;
    top: 50%;
    height: 1px;
    transform: translateY(-0.5px);
  `
      : `
    top: 0;
    bottom: 0;
    left: 50%;
    width: 1px;
    transform: translateX(-0.5px);
  `}
`;

const HorizontalAxisLabel = styled.div`
  text-align: center;
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textRegular};
  user-select: none;
`;

const ItemsSection = styled.div<{ $isEditing?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 12px;
  height: 100%;
  justify-content: center;

  ${({ $isEditing }) =>
    $isEditing &&
    `
    pointer-events: none;
    opacity: 0.5;
  `}
`;

const RemoveButton = styled.i`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.textRegular};
  font-size: 12px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px 4px 4px 8px;
  margin: -4px -4px -4px 4px;
  transition: all 0.2s ease-in-out;
  flex-shrink: 0;

  &:hover {
    color: ${({ theme }) => theme.colors.textPrimary};
    background: none;
  }
`;

const PlacedItem = styled.div<{ x: number; y: number; isDragging?: boolean }>`
  position: absolute;
  left: ${({ x }) => x * 100}%;
  top: ${({ y }) => (1 - y) * 100}%;
  z-index: ${({ isDragging }) => (isDragging ? 1000 : 10)};
  cursor: grab;
  max-width: 120px;
  touch-action: none;

  &:active {
    cursor: grabbing;
  }
`;

const PlacedItemContent = styled.div<{ hasNote?: boolean }>`
  display: flex;
  align-items: center;
  background-color: #2e2e2f;
  border: 1px solid
    ${({ theme, hasNote }) =>
      hasNote ? theme.colors.primary : theme.colors.textActive};
  border-radius: 8px;
  padding: 4px 8px;
  box-shadow: ${({ theme }) => theme.shadows.default};
  gap: 2px;
`;

const PlacedItemText = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-weight: 500;
  user-select: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
`;

const UnplacedItemsContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 12px;
  flex-wrap: wrap;
`;

const AnnotationBubble = styled.div<{ $left: number; $top: number }>`
  position: fixed;
  left: ${({ $left }) => $left}px;
  top: ${({ $top }) => $top}px;
  transform: translate(-50%, calc(-100% - 16px));
  z-index: 1100;
  border-radius: 10px;
  min-width: 512px;
  max-width: 512px;
  box-shadow: ${({ theme }) => theme.shadows.default};

  > div {
    padding-bottom: 0;
  }

  /* Arrow pointing down */
  &::after {
    content: '';
    position: absolute;
    bottom: -7px;
    left: 50%;
    transform: translateX(-50%);
    width: 14px;
    height: 14px;
    background-color: #222223;
    border-right: 1px solid ${({ theme }) => theme.colors.bgActive};
    border-bottom: 1px solid ${({ theme }) => theme.colors.bgActive};
    transform: translateX(-50%) rotate(45deg);
  }
`;

const AnnotationInput = styled(MultiLineInput)`
  margin-top: 0;
`;

export default function MatrixRankingSection({
  section,
  stepId,
}: {
  section: Extract<
    AssessmentTestContextType['steps'][number]['sections'][number],
    { type: 'matrix-ranking' }
  >;
  stepId: string;
}) {
  const {
    answer,
    content,
    items,
    horizontalAxisLabel,
    verticalAxisLabel,
    annotationPrompt,
  } = section;
  const [, dispatch] = React.useContext(AssessmentTestContext);
  const matrixRef = React.useRef<HTMLDivElement | null>(null);
  const annotationBubbleRef = React.useRef<HTMLDivElement | null>(null);
  const [editingItem, setEditingItem] = React.useState<string | null>(null);
  const [editingNote, setEditingNote] = React.useState('');
  const [activeId, setActiveId] = React.useState<string | null>(null);
  const dragOffsetRef = React.useRef<{ x: number; y: number } | null>(null);
  const [bubblePosition, setBubblePosition] =
    React.useState<{
      left: number;
      top: number;
    } | null>(null);
  const [animationParent] = useAutoAnimate<HTMLDivElement>();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    })
  );

  const setAnswer = React.useCallback(
    (value: Array<{ item: string; x: number; y: number; note: string }>) => {
      dispatch({
        type: 'CHANGE_ANSWER_MATRIX_RANKING',
        payload: {
          stepId,
          sectionId: section.id,
          answer: value,
        },
      });
    },
    [stepId, section.id, dispatch]
  );

  const { onDragStart, wrapOnDragEnd } = useDndScrollLock();

  const unplacedItems = React.useMemo(() => {
    return items.filter((item) => !answer.find((a) => a.item === item.id));
  }, [items, answer]);

  // Collision detection helper
  const checkOverlap = React.useCallback(
    (
      x1: number,
      y1: number,
      x2: number,
      y2: number,
      itemWidth: number,
      itemHeight: number,
      matrixWidth: number,
      matrixHeight: number,
      padding = 8 // px padding between items
    ) => {
      // Convert normalized coordinates to pixel positions
      const left1 = x1 * matrixWidth;
      const top1 = (1 - y1) * matrixHeight;
      const right1 = left1 + itemWidth;
      const bottom1 = top1 + itemHeight;

      const left2 = x2 * matrixWidth;
      const top2 = (1 - y2) * matrixHeight;
      const right2 = left2 + itemWidth;
      const bottom2 = top2 + itemHeight;

      // Check if rectangles overlap (with padding)
      return !(
        right1 + padding < left2 ||
        left1 - padding > right2 ||
        bottom1 + padding < top2 ||
        top1 - padding > bottom2
      );
    },
    []
  );

  // Find nearest available position
  const findNearestAvailablePosition = React.useCallback(
    (
      targetX: number,
      targetY: number,
      excludeItem: string | null,
      matrixWidth: number,
      matrixHeight: number
    ) => {
      const itemWidth = 120;
      const itemHeight = 40;
      const maxX = 1 - itemWidth / matrixWidth;
      const minY = itemHeight / matrixHeight;

      // Filter out the item being moved
      const otherItems = answer.filter((a) => a.item !== excludeItem);

      // If no overlap with any item, return the clamped original position
      const hasOverlap = otherItems.some((other) =>
        checkOverlap(
          targetX,
          targetY,
          other.x,
          other.y,
          itemWidth,
          itemHeight,
          matrixWidth,
          matrixHeight
        )
      );

      if (!hasOverlap) {
        return { x: targetX, y: targetY };
      }

      // Search in a spiral pattern for the nearest free space
      const step = 0.02; // Step size in normalized coordinates (~2% of matrix)
      const maxSearchRadius = 0.5; // Search up to 50% of matrix size

      for (let radius = step; radius <= maxSearchRadius; radius += step) {
        const points = 24; // Number of points to check at each radius
        for (let i = 0; i < points; i++) {
          const angle = (i / points) * 2 * Math.PI;
          let testX = targetX + Math.cos(angle) * radius;
          let testY = targetY + Math.sin(angle) * radius;

          // Clamp to matrix bounds
          testX = Math.max(0, Math.min(maxX, testX));
          testY = Math.max(minY, Math.min(1, testY));

          // Check if this position is free
          const hasCollision = otherItems.some((other) =>
            checkOverlap(
              testX,
              testY,
              other.x,
              other.y,
              itemWidth,
              itemHeight,
              matrixWidth,
              matrixHeight
            )
          );

          if (!hasCollision) {
            return { x: testX, y: testY };
          }
        }
      }

      // If no position found, return original (shouldn't happen in practice)
      return { x: targetX, y: targetY };
    },
    [answer, checkOverlap]
  );

  const onDragStartInternal = React.useCallback(
    (event: DragStartEvent) => {
      // Prevent dragging when annotation bubble is open
      if (editingItem) {
        return;
      }

      const activeId = String(event.active.id);
      setActiveId(activeId);

      // Store the offset from the item's position to where we grabbed it
      if (activeId.startsWith('placed:')) {
        const item = activeId.replace('placed:', '');
        const placedItem = answer.find((a) => a.item === item);
        if (placedItem && matrixRef.current) {
          const matrixRect = matrixRef.current.getBoundingClientRect();
          const itemScreenX = matrixRect.left + placedItem.x * matrixRect.width;
          const itemScreenY =
            matrixRect.top + (1 - placedItem.y) * matrixRect.height;
          const pointerX = (event.activatorEvent as PointerEvent).clientX;
          const pointerY = (event.activatorEvent as PointerEvent).clientY;

          dragOffsetRef.current = {
            x: pointerX - itemScreenX,
            y: pointerY - itemScreenY,
          };
        }
      } else if (activeId.startsWith('unplaced:')) {
        // For unplaced items, we want the drop position to be centered at the cursor
        // The drag overlay shows a PlacedItemContent which will be ~120px wide, ~40px tall
        // Since PlacedItem positions by top-left, we need to offset by half the size
        // to center it at the cursor
        dragOffsetRef.current = {
          x: 60, // Half of estimated width (120px / 2)
          y: 20, // Half of estimated height (40px / 2)
        };
      } else {
        dragOffsetRef.current = null;
      }
    },
    [answer, editingItem]
  );

  const onDragStartCombined = React.useCallback(
    (event: DragStartEvent) => {
      onDragStart();
      onDragStartInternal(event);
    },
    [onDragStart, onDragStartInternal]
  );

  const onDragEndInternal = React.useCallback(
    (event: DragEndEvent) => {
      const { active } = event;
      const draggedItemId = String(active.id);
      setActiveId(null);

      // Get the final position of the drag
      const matrixRect = matrixRef.current?.getBoundingClientRect();
      if (!matrixRect) return;

      // Calculate the final pointer position
      const pointerX =
        (event.activatorEvent as PointerEvent).clientX + (event.delta?.x || 0);
      const pointerY =
        (event.activatorEvent as PointerEvent).clientY + (event.delta?.y || 0);

      // Check if dropped within the matrix bounds
      if (
        pointerX >= matrixRect.left &&
        pointerX <= matrixRect.right &&
        pointerY >= matrixRect.top &&
        pointerY <= matrixRect.bottom
      ) {
        // Account for grab offset when dropping
        let adjustedPointerX = pointerX;
        let adjustedPointerY = pointerY;

        if (dragOffsetRef.current) {
          adjustedPointerX -= dragOffsetRef.current.x;
          adjustedPointerY -= dragOffsetRef.current.y;
        }

        // Estimate item dimensions for clamping (max-width: 120px from styled component)
        const estimatedItemWidth = 120;
        const estimatedItemHeight = 40; // Approximate height of the pill

        // Calculate normalized position (0-1) - this should be where top-left corner is
        // For unplaced items, we want to center them at the cursor position
        // For placed items, the offset already accounts for where they were grabbed
        let x = (adjustedPointerX - matrixRect.left) / matrixRect.width;
        let y = 1 - (adjustedPointerY - matrixRect.top) / matrixRect.height;

        // For x: clamp between 0 (left edge) and 1-width (right edge touches border)
        const maxX = 1 - estimatedItemWidth / matrixRect.width;
        x = Math.max(0, Math.min(maxX, x));

        // For y: clamp between height (bottom edge touches border) and 1 (top edge at top)
        const minY = estimatedItemHeight / matrixRect.height;
        y = Math.max(minY, Math.min(1, y));

        if (draggedItemId.startsWith('unplaced:')) {
          // Placing a new item - find nearest available position
          const item = draggedItemId.replace('unplaced:', '');
          const finalPosition = findNearestAvailablePosition(
            x,
            y,
            null, // No item to exclude for new placements
            matrixRect.width,
            matrixRect.height
          );
          const next = [
            ...answer,
            { item, x: finalPosition.x, y: finalPosition.y, note: '' },
          ];
          setAnswer(next);
          setEditingItem(item);
          setEditingNote('');
        } else if (draggedItemId.startsWith('placed:')) {
          // Moving an existing item - find nearest available position
          const item = draggedItemId.replace('placed:', '');
          const existingAnswer = answer.find((a) => a.item === item);
          if (existingAnswer) {
            const finalPosition = findNearestAvailablePosition(
              x,
              y,
              item, // Exclude this item from collision checks
              matrixRect.width,
              matrixRect.height
            );
            const next = answer.map((a) =>
              a.item === item
                ? { ...a, x: finalPosition.x, y: finalPosition.y }
                : a
            );
            setAnswer(next);
          }
        }

        dragOffsetRef.current = null;
      }
    },
    [answer, setAnswer, findNearestAvailablePosition]
  );

  const onDragEnd = React.useMemo(
    () => wrapOnDragEnd<DragEndEvent>(onDragEndInternal),
    [wrapOnDragEnd, onDragEndInternal]
  );

  const handleSaveNote = React.useCallback(() => {
    if (editingItem) {
      const next = answer.map((a) =>
        a.item === editingItem ? { ...a, note: editingNote } : a
      );
      setAnswer(next);
      setEditingItem(null);
      setEditingNote('');
    }
  }, [editingItem, editingNote, answer, setAnswer]);

  const handleCancelEdit = React.useCallback(() => {
    setEditingItem(null);
    setEditingNote('');
  }, []);

  const handleRemoveItem = React.useCallback(
    (item: string) => {
      const next = answer.filter((a) => a.item !== item);
      setAnswer(next);
    },
    [answer, setAnswer]
  );

  // Calculate bubble position when editing item changes
  React.useEffect(() => {
    if (!editingItem || !matrixRef.current) {
      setBubblePosition(null);
      return;
    }

    const item = answer.find((a) => a.item === editingItem);
    if (!item) {
      setBubblePosition(null);
      return;
    }

    const matrixRect = matrixRef.current.getBoundingClientRect();
    const left = matrixRect.left + item.x * matrixRect.width + 60;
    const top = matrixRect.top + (1 - item.y) * matrixRect.height;

    setBubblePosition({ left, top });
  }, [editingItem, answer]);

  // Handle clicking outside the annotation bubble to close it
  React.useEffect(() => {
    if (!editingItem) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        annotationBubbleRef.current &&
        !annotationBubbleRef.current.contains(event.target as Node)
      ) {
        handleSaveNote();
      }
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleCancelEdit();
      }
    };

    // Add a small delay to prevent immediate closure when the bubble appears
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [editingItem, handleSaveNote, handleCancelEdit]);

  React.useEffect(() => {
    if (editingItem) {
      return;
    }

    const scrollContainer = document.querySelector(
      '[data-scroll-container="true"]'
    ) as HTMLElement | null;

    if (scrollContainer) {
      scrollContainer.style.overflow = editingItem ? 'hidden' : '';
      return () => {
        scrollContainer.style.overflow = '';
      };
    }
  }, [editingItem]);

  return (
    <SectionWrapper>
      <NonCopyableMarkdown md={content} />

      <DndContext
        sensors={sensors}
        onDragStart={onDragStartCombined}
        onDragEnd={onDragEnd}
      >
        <MatrixContainer>
          <MatrixWrapper>
            <AxisLabelsContainer>
              <VerticalAxisLabel>{verticalAxisLabel}</VerticalAxisLabel>
              <MatrixAndHorizontalLabel>
                <MatrixGrid ref={matrixRef}>
                  <MatrixInner $isEditing={!!editingItem}>
                    <AxisLine direction="horizontal" />
                    <AxisLine direction="vertical" />
                    {answer.map((placedItem) => (
                      <PlacedItemDraggable
                        key={placedItem.item}
                        item={{
                          id: placedItem.item,
                          content:
                            items.find((i) => i.id === placedItem.item)
                              ?.content || '',
                          x: placedItem.x,
                          y: placedItem.y,
                          note: placedItem.note,
                        }}
                        annotationPrompt={
                          annotationPrompt ||
                          'Add a ≤10-word note for your placement of each option.'
                        }
                        onRemove={handleRemoveItem}
                        onCancelEdit={handleCancelEdit}
                        isEditing={editingItem === placedItem.item}
                      />
                    ))}
                  </MatrixInner>
                </MatrixGrid>
                <HorizontalAxisLabel>{horizontalAxisLabel}</HorizontalAxisLabel>
              </MatrixAndHorizontalLabel>
            </AxisLabelsContainer>
          </MatrixWrapper>

          <ItemsSection $isEditing={!!editingItem}>
            {unplacedItems.length > 0 ? (
              <>
                <UnplacedItemsContainer ref={animationParent}>
                  {unplacedItems.map((item) => (
                    <UnplacedItemDraggable key={item.id} item={item} />
                  ))}
                </UnplacedItemsContainer>
                <Label>Drag items onto the matrix</Label>
              </>
            ) : (
              <Label
                style={{
                  textAlign: 'center',
                }}
              >
                You've placed all the items.
              </Label>
            )}
          </ItemsSection>
        </MatrixContainer>
        <DragOverlay>
          {activeId
            ? (() => {
                if (activeId.startsWith('placed:')) {
                  const itemId = activeId.replace('placed:', '');
                  const placedItem = answer.find((a) => a.item === itemId);
                  const itemContent =
                    items.find((i) => i.id === itemId)?.content || itemId;
                  return (
                    <PlacedItemContent hasNote={!!placedItem?.note}>
                      <PlacedItemText>
                        <NonCopyableSpan>{itemContent}</NonCopyableSpan>
                      </PlacedItemText>
                      <RemoveButton className="fa-solid fa-xmark" />
                    </PlacedItemContent>
                  );
                } else {
                  // Show unplaced items in the same style as placed items for smooth transition
                  const itemId = activeId.replace('unplaced:', '');
                  const itemContent =
                    items.find((i) => i.id === itemId)?.content || itemId;
                  return (
                    <PlacedItemContent hasNote={false}>
                      <PlacedItemText>
                        <NonCopyableSpan>{itemContent}</NonCopyableSpan>
                      </PlacedItemText>
                      <RemoveButton className="fa-solid fa-xmark" />
                    </PlacedItemContent>
                  );
                }
              })()
            : null}
        </DragOverlay>
      </DndContext>

      <FollowUp section={section} stepId={stepId} />

      {/* Render annotation bubble in a portal */}
      {editingItem &&
        bubblePosition &&
        ReactDOM.createPortal(
          <AnnotationBubble
            ref={annotationBubbleRef}
            $left={bubblePosition.left}
            $top={bubblePosition.top}
          >
            <AnnotationInput
              submitIconClassName="fas fa-check"
              placeholder={
                annotationPrompt ||
                'Add a ≤10-word note for your placement of each option.'
              }
              value={editingNote}
              minRows={1}
              maxRows={4}
              onChange={(e) => setEditingNote(e.target.value)}
              onBlur={handleSaveNote}
              onSubmit={handleSaveNote}
              shouldSubmitOnEnter={true}
            />
          </AnnotationBubble>,
          document.body
        )}
    </SectionWrapper>
  );
}

function UnplacedItemDraggable({
  item,
}: {
  item: { id: string; content: string };
}) {
  const itemId = `unplaced:${item.id}`;
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: itemId,
  });

  const style: React.CSSProperties = {
    cursor: 'grab',
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      id={itemId}
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      tabIndex={-1}
    >
      <StaticOption isSelected={false}>
        <NonCopyableMarkdownOption md={item.content} />
      </StaticOption>
    </div>
  );
}

function PlacedItemDraggable({
  item,
  annotationPrompt,
  onRemove,
  onCancelEdit,
  isEditing,
}: {
  item: { id: string; content: string; x: number; y: number; note: string };
  annotationPrompt: string;
  onRemove: (item: string) => void;
  onCancelEdit: () => void;
  isEditing: boolean;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `placed:${item.id}`,
  });

  const style: React.CSSProperties = {
    opacity: isDragging ? 0 : 1,
  };

  const tooltipId = `matrix-item-${item.id.replace(/\s+/g, '-')}`;

  return (
    <>
      <PlacedItem
        ref={setNodeRef}
        x={item.x}
        y={item.y}
        isDragging={isDragging}
        style={style}
        data-draggable="true"
        {...attributes}
        {...listeners}
        {...(!isDragging && { 'data-tooltip-id': tooltipId })}
      >
        <PlacedItemContent hasNote={!!item.note}>
          <PlacedItemText>
            <NonCopyableSpan>{item.content}</NonCopyableSpan>
          </PlacedItemText>
          <RemoveButton
            onPointerDown={(e) => {
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.stopPropagation();
              // If this item is being edited, cancel the edit instead of removing
              if (isEditing) {
                onCancelEdit();
              } else {
                onRemove(item.id);
              }
            }}
            style={
              isEditing
                ? {
                    pointerEvents: 'auto',
                    zIndex: 1002,
                  }
                : undefined
            }
            className="fa-solid fa-xmark"
          />
        </PlacedItemContent>
      </PlacedItem>
      <SmallPortalTooltip
        anchorSelect={`[data-tooltip-id='${tooltipId}']`}
        place="top"
        isOpen={!isDragging && !isEditing ? undefined : false}
        style={{ maxWidth: '300px' }}
      >
        <TooltipContent>
          <TooltipTitle>{item.content}</TooltipTitle>
          <TooltipDetail
            style={{ opacity: 0.7, fontStyle: 'italic', marginBottom: 8 }}
          >
            <TooltipDetailHighlight> {annotationPrompt}</TooltipDetailHighlight>
          </TooltipDetail>
          {item.note ? (
            <TooltipDetail>{item.note}</TooltipDetail>
          ) : (
            <TooltipDetail style={{ opacity: 0.6 }}>
              No note left for this item.
            </TooltipDetail>
          )}
        </TooltipContent>
      </SmallPortalTooltip>
    </>
  );
}
